import { useState } from 'react';
import { Wifi, Clock, Globe, CheckCircle2, Smartphone, ArrowRight, PhoneCall } from 'lucide-react';
import { Plan } from '../types';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface PlanCardProps {
  plan: Plan;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  className?: string;
}

export const PlanCard = ({ 
  plan, 
  isSelected = false, 
  onSelect,
  className = '' 
}: PlanCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [logoError, setLogoError] = useState(false);
  
  const handleSelect = () => {
    if (onSelect) {
      onSelect(plan.id);
    }
  };
  
  const formatDataAllowance = (data: string) => {
    // We've standardized to "Ubegrænset" for unlimited data
    return data;
  };
  
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };
  
  const hasDiscount = !!plan.original_price && plan.original_price > plan.price;
  const discountPercentage = hasDiscount 
    ? Math.round(((plan.original_price - plan.price) / plan.original_price) * 100) 
    : 0;

  return (
    <div 
      className={`relative plan-card ${
        isSelected ? 'ring-2 ring-primary' : ''
      } ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Provider badge */}
      <div className="absolute top-6 left-6">
        <Badge variant="outline" className="font-medium text-xs px-2 py-1 bg-white/80 backdrop-blur-sm flex items-center gap-2">
          {plan.logo_url && !logoError && (
            <img
              src={plan.logo_url}
              alt={`${plan.provider} logo`}
              className="h-4 w-4 object-contain"
              onError={() => setLogoError(true)}
            />
          )}
          {plan.provider}
        </Badge>
      </div>
      
      {/* Discount badge - improved contrast */}
      {hasDiscount && (
        <div className="absolute top-6 inset-x-0 flex justify-center">
          <div className="discount-badge">
            Spar {discountPercentage}%
          </div>
        </div>
      )}
      
      {/* Selection checkbox (if selectable) */}
      {onSelect && (
        <div className="absolute top-6 right-6">
          <button
            onClick={handleSelect}
            aria-label={isSelected ? `Fravælg ${plan.name}` : `Vælg ${plan.name}`}
            aria-pressed={isSelected}
            className={`h-6 w-6 rounded-full border-2 flex items-center justify-center transition-colors ${
              isSelected 
                ? 'bg-primary border-primary text-white' 
                : 'border-gray-400 bg-white/80'
            }`}
          >
            {isSelected && <CheckCircle2 className="h-5 w-5" />}
          </button>
        </div>
      )}
      
      <div className="p-6 pt-16">
        <div className="mb-6">
          <h3 className="text-lg font-semibold">{plan.name}</h3>
          <p className="text-sm text-foreground/80 line-clamp-2">{plan.description}</p>
        </div>
        
        <div className="mb-6">
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">{formatPrice(plan.price)}</span>
            <span className="text-foreground/80 ml-1">/md</span>
            
            {hasDiscount && (
              <span className="ml-2 text-foreground/70 text-sm line-through">
                {formatPrice(plan.original_price!)}
              </span>
            )}
          </div>

          {/*
          plan.price_label && (
            <p className="text-xs text-foreground/70 mt-1">{plan.price_label}</p>
          )*/}
        </div>
        
        <div className="space-y-4 mb-6">
          {/* Data allowance */}
          <div className="flex items-center">
            <Wifi className="h-5 w-5 text-primary mr-3" />
            <span className="font-medium">{formatDataAllowance(plan.data_allowance)}</span>
          </div>

          {/* Talk time */}
          <div className="flex items-center">
            <PhoneCall className="h-5 w-5 text-primary mr-3" />
            <span>{plan.minutes}</span>
          </div>
          
          {/* Contract length */}
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-primary mr-3" />
            <span>{plan.contract_length}</span>
          </div>
          
          {/* 5G Support */}
          <div className="flex items-center">
            <Smartphone className="h-5 w-5 text-primary mr-3" />
            <span>{plan.has_5g ? '5G Netværk' : '4G Netværk'}</span>
          </div>
          
          {/* Roaming */}
          <div className="flex items-center">
            <Globe className="h-5 w-5 text-primary mr-3" />
            <span>Roaming: {plan.roaming}</span>
          </div>
        </div>
        
        {/* Features - fixed contrast issues */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-2">Fordele</h4>
          <ul className="space-y-2">
            {plan.features.slice(0, 3).map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle2 className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                <span className="feature-item">{feature}</span>
              </li>
            ))}
            {plan.features.length > 3 && (
              <li className="more-features">
                +{plan.features.length - 3} flere fordele
              </li>
            )}
          </ul>
        </div>
        
        <div className="flex items-center justify-between mt-auto">
          <Button 
            asChild 
            className={`w-full group transition-all duration-300 ${
              isHovered ? 'bg-primary' : 'bg-primary/90'
            }`}
            aria-label={`Se tilbud for ${plan.name} fra ${plan.provider}`}
          >
            <a href={plan.link} target="_blank" rel="noopener noreferrer">
              Se tilbud
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};
